import React from 'react'
import { AlertTriangle, ExternalLink } from 'lucide-react'

const SEVERITY_COLOR = {
  low: 'var(--onds-text-dim)',
  medium: 'var(--onds-caution)',
  high: 'var(--onds-bear)',
  'thesis-breaking': 'var(--onds-bear)',
}

const STATUS_COLOR = {
  unrefuted: 'var(--onds-bear)',
  'partially refuted': 'var(--onds-caution)',
  refuted: 'var(--onds-bull)',
}

const SEVERITY_RANK = { 'thesis-breaking': 0, high: 1, medium: 2, low: 3 }

const colorOf = (map, key) => map[(key ?? '').toLowerCase()] ?? 'var(--onds-text-dim)'

function Chip({ label, color }) {
  if (!label) return null
  return (
    <span className="onds-chip" style={{ color, borderColor: `${color}55`, backgroundColor: `${color}14` }}>
      {label}
    </span>
  )
}

function Field({ label, text, color }) {
  if (!text) return null
  return (
    <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed">
      <span className="font-semibold" style={{ color: color ?? 'var(--onds-text)' }}>{label}: </span>{text}
    </p>
  )
}

function ClaimCard({ claim }) {
  return (
    <div className="rounded-xl p-4 border" style={{ backgroundColor: 'var(--onds-surface)', borderColor: 'var(--onds-border)' }}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{claim.source}</span>
          {claim.type && <span className="text-[11px] text-[var(--onds-text-dim)]">{claim.type}</span>}
          {claim.date && <span className="text-[11px] text-[var(--onds-text-dim)]">· {claim.date}</span>}
          {claim.url && (
            <a href={claim.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-[var(--onds-accent)]">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Chip label={claim.status} color={colorOf(STATUS_COLOR, claim.status)} />
          <Chip label={claim.severity} color={colorOf(SEVERITY_COLOR, claim.severity)} />
        </div>
      </div>
      <p className="text-sm text-[var(--onds-text)] leading-relaxed mb-2">{claim.allegation}</p>
      <div className="flex flex-col gap-1">
        <Field label="Evidence" text={claim.evidence} />
        <Field label="Company response" text={claim.companyResponse} />
        <Field label="Implication" text={claim.implication} color="var(--onds-bear)" />
      </div>
    </div>
  )
}

export default function BearCasePanel({ bearCase }) {
  if (!bearCase || (!bearCase.summary && !(bearCase.claims?.length))) return null

  const severityColor = colorOf(SEVERITY_COLOR, bearCase.severity)
  const claims = [...(bearCase.claims ?? [])].sort(
    (a, b) => (SEVERITY_RANK[(a.severity ?? '').toLowerCase()] ?? 9) - (SEVERITY_RANK[(b.severity ?? '').toLowerCase()] ?? 9),
  )

  return (
    <div className="onds-card p-5 md:p-6" style={{ borderColor: `${severityColor}66` }}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" style={{ color: 'var(--onds-bear)' }} />
          <h2 className="text-lg font-bold">Bear Case</h2>
          <span className="text-sm text-[var(--onds-text-dim)]">short reports, lawsuits and red flags</span>
        </div>
        <div className="flex items-center gap-2">
          {bearCase.thesisAtRisk && <Chip label="thesis at risk" color="var(--onds-bear)" />}
          {bearCase.severity && <Chip label={`${bearCase.severity} severity`} color={severityColor} />}
        </div>
      </div>

      {bearCase.summary && (
        <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed mb-4">{bearCase.summary}</p>
      )}

      <div className="flex flex-col gap-3">
        {claims.map((claim, i) => <ClaimCard key={i} claim={claim} />)}
      </div>
    </div>
  )
}
